import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { matchPath, Navigate, Route, useLocation } from 'react-router-dom';
import './i18n/i18n';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { catalogPlugin } from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { apis } from './apis';
import { searchPage } from './components/search/SearchPage';
import { projectDetailsPage, projectsPage } from './components/projects';
import { useParams } from 'react-router-dom';
import { TestCaseGenerationPage } from './components/test-case-generation';

import {
  AlertDisplay,
  OAuthRequestDialog,
  Progress,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

import { EmailSignInPage, NoAccessPage } from './components/SignIn';
import { NO_ACCESS_PATH } from './utils/noAccess';
import { RequirementDoctorPage } from './components/requirement-doctor';
import { RequirementLifecyclePage } from './components/requirement-lifecycle/RequirementLifecyclePage';
import { HomePage, RootRedirect } from './components/home';
import { PRODUCT_HOME_PATH, PRODUCT_LANDING_PATH } from './utils/authRedirect';
import { orgAdminPage } from './components/orgAdmin';
import { UnifiedThemeProvider } from '@backstage/theme';
import { darkThemeSystemFont, lightThemeSystemFont } from './theme/theme';
import { DashboardShell } from './components/dashboard/DashboardShell';
import { DashboardRedirect } from './components/dashboard/DashboardRedirect';
import { KnowledgeBasePage } from './components/knowledgeBase';
import { AuthGuard } from './components/auth/AuthGuard';
import { ActiveProjectProvider } from './components/activeProject';
import { CurrentUserProvider } from './components/currentUser';

// Backstage's own portal sign-in is out of scope here (separate concern from
// the product's own login, see CB25SES-1200) — the shell always uses its
// default behavior, in every environment. The product's login lives at the
// routed `/login` page below (CB25SES-1197/1206), not as a Backstage
// `SignInPage`; this is also why /admin/* below needs no bypass logic of its
// own to satisfy the "admin auth isolated from customer auth" AC (#8).

// Admin UI (CB25SES-864 scaffold) is mounted as a lazily-loaded route-level
// bundle so it never bloats the main app's initial load, per the scaffold's
// "favor code-splitting/lazy-loading" requirement.
const AdminApp = lazy(() =>
  import('./components/admin/AdminApp').then(m => ({ default: m.AdminApp })),
);

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  // No registered 'auto' theme: Backstage's own AppThemeProvider already
  // implements "follow the OS preference" when no theme is explicitly
  // selected (appThemeApi.getActiveThemeId() === undefined) - it picks
  // whichever of these two entries has the matching 'variant', reactively,
  // via its own prefers-color-scheme listener. An earlier version of this
  // registered a third 'auto' AppTheme with its own Provider doing the same
  // OS check, but AppTheme.variant is a single static 'light' | 'dark' value
  // per entry, so that theme's variant metadata always read 'light' even
  // while actually rendering dark - any other consumer of
  // getInstalledThemes() reading .variant (not just this app's own code)
  // would see a mismatch. SettingsDialog's handleSave maps its 'auto' UI
  // choice to setActiveThemeId(undefined) to use this built-in behavior.
  themes: [
    {
      id: 'light',
      title: 'Light',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider
          theme={lightThemeSystemFont}
          children={children}
        />
      ),
    },
    {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={darkThemeSystemFont} children={children} />
      ),
    },
  ],
});

function TestCaseGenerationRoute() {
  const { projectId = '' } = useParams();

  return <TestCaseGenerationPage projectId={projectId} />;
}

const routes = (
  <FlatRoutes>
    <Route path="/" element={<RootRedirect />} />
    <Route path={PRODUCT_HOME_PATH} element={<HomePage />} />

    {/* Dashboard (deep-linkable per project) */}
    <Route
      path="/dashboard/knowledge-bases/:kbId"
      element={<KnowledgeBasePage />}
    />

    <Route path="/dashboard" element={<DashboardRedirect />} />

    {/* Catalog and API Docs are not part of the product UI. Keep the paths
        so bookmarks and return_to=/catalog land on the product page instead
        of a Backstage page whose backend rejects the SES session. */}
    <Route
      path="/catalog/*"
      element={<Navigate to={PRODUCT_LANDING_PATH} replace />}
    />
    <Route
      path="/api-docs/*"
      element={<Navigate to={PRODUCT_LANDING_PATH} replace />}
    />

    {/* TechDocs */}
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>

    {/* Scaffolder / APIs / Import */}
    <Route path="/create" element={<ScaffolderPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />

    {/* Search */}
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>

    {/* Projects */}
    <Route path="/projects" element={projectsPage} />
    <Route path="/projects/:projectId" element={projectDetailsPage} />

    {/* Requirements */}
    <Route path="/requirements" element={<RequirementLifecyclePage />} />
    <Route path="/requirement-doctor" element={<RequirementDoctorPage />} />
    <Route path="/org-admin" element={orgAdminPage} />

    {/* Graph / notifications */}
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/login" element={<EmailSignInPage />} />
    <Route path={NO_ACCESS_PATH} element={<NoAccessPage />} />
    <Route
      path="/admin/*"
      element={
        <Suspense fallback={<Progress />}>
          <AdminApp />
        </Suspense>
      }
    />

    <Route
      path="/dashboard/:projectId"
      element={<Navigate to="test-case-generation" replace />}
    />

    <Route
      path="/dashboard/:projectId/test-case-generation"
      element={<TestCaseGenerationRoute />}
    />
  </FlatRoutes>
);

function AppRouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const isPublicRoute =
    pathname === '/' || pathname === '/login' || pathname === NO_ACCESS_PATH;

  if (isPublicRoute || matchPath('/admin/*', pathname)) {
    return <>{children}</>;
  }

  return (
    <AuthGuard>
      {/*
       * The active-project provider sits inside AuthGuard on purpose: it
       * fetches GET /projects as soon as it mounts, and mounting it above the
       * guard would fire that call on the public routes ("/" and "/login")
       * where there is no session, turning every unauthenticated visit into a
       * 401 redirect loop. CurrentUserProvider (GET /users/me) sits there for
       * the same reason.
       */}
      <CurrentUserProvider>
        <ActiveProjectProvider>
          <DashboardShell>{children}</DashboardShell>
        </ActiveProjectProvider>
      </CurrentUserProvider>
    </AuthGuard>
  );
}

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <AppRouteBoundary>{routes}</AppRouteBoundary>
    </AppRouter>
  </>,
);
